import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './onboarding.css';

import StepAccount from './StepAccount';
import StepVerify from './StepVerify';
import StepBusiness from './StepBusiness';
import StepPlan from './StepPlan';
import StepNumber from './StepNumber';
import StepSuccess from './StepSuccess';

const STEPS = [
  { num: 1, label: 'Account' },
  { num: 2, label: 'Verify' },
  { num: 3, label: 'Business' },
  { num: 4, label: 'Plan' },
  { num: 5, label: 'Go Live' },
];

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [data, setData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    businessName: '',
    location: '',
    businessDescription: '',
    languages: ['English'],
    plan: 'growth',
    virtualNumber: '',
    forwardingCode: '',
    token: '',
  });

  const updateData = (updates) => {
    setData(prev => ({ ...prev, ...updates }));
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 6));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  // Called when customer reaches Step 5 (Go Live)
  // This is where we actually create the account and provision the number
  const handleCreateAccount = async () => {
    setLoading(true);
    setSignupError('');

    try {
      const response = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          password: data.password,
          business_name: data.businessName,
          location: data.location,
          business_description: data.businessDescription,
          languages: data.languages,
          plan: data.plan,
        })
      });

      const result = await response.json();

      if (!result.success) {
        setSignupError(result.error || 'Signup failed. Please try again.');
        setLoading(false);
        return false;
      }

      // Store token and customer data
      localStorage.setItem('cia_token', result.token);
      localStorage.setItem('cia_customer', JSON.stringify(result.customer));

      updateData({
        token: result.token,
        virtualNumber: result.virtual_number,
        forwardingCode: result.forwarding_code,
        forwardingCodes: result.forwarding_codes || {},
      });

      setLoading(false);
      return true;

    } catch (e) {
      console.error('Signup error:', e);
      setSignupError('Connection failed. Make sure the server is running.');
      setLoading(false);
      return false;
    }
  };

  const handleStepFourNext = async () => {
    // When they confirm plan — create account and provision number
    const success = await handleCreateAccount();
    if (success) nextStep();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <StepAccount data={data} updateData={updateData} onNext={nextStep} />;
      case 2: return <StepVerify data={data} updateData={updateData} onNext={nextStep} onBack={prevStep} />;
      case 3: return <StepBusiness data={data} updateData={updateData} onNext={nextStep} onBack={prevStep} />;
      case 4: return (
        <StepPlan
          data={data}
          updateData={updateData}
          onNext={handleStepFourNext}
          onBack={prevStep}
          loading={loading}
          error={signupError}
        />
      );
      case 5: return <StepNumber data={data} updateData={updateData} onNext={nextStep} onBack={prevStep} />;
      case 6: return <StepSuccess data={data} onDashboard={() => navigate('/')} />;
      default: return null;
    }
  };

  return (
    <div className="ob-container">
      <header className="ob-header">
        <div className="ob-logo">
          <div className="ob-logo-icon">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="14" cy="14" r="11" stroke="#00C896" strokeWidth="2.4"/>
              <circle cx="14" cy="14" r="4" fill="#00C896"/>
              <path d="M19 18.5L21.2 20.7L25.5 15.5" stroke="#3B82F6" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="ob-logo-text">Aizaan</span>
        </div>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {currentStep < 6 ? `Step ${currentStep} of 5` : 'Setup Complete'}
        </span>
      </header>

      {currentStep < 6 && (
        <div style={{ padding: '16px 20px 0', maxWidth: 540, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          <div className="ob-step-indicator">
            {STEPS.map((step, idx) => (
              <div key={step.num} className="ob-step">
                <div className={`ob-step-dot ${currentStep > step.num ? 'completed' : currentStep === step.num ? 'active' : ''}`}>
                  {currentStep > step.num ? '✓' : step.num}
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`ob-step-line ${currentStep > step.num ? 'completed' : ''}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ob-body">
        {renderStep()}
      </div>
    </div>
  );
}
